import { useMemo } from "react";
import { currencyFormatter, formatDate } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import Card from "@/components/custom/card";
import { Currency, Invoice } from "@/lib/types";
import { Mixpanel } from "@/lib/mixpanel";
import { INVOICE_STATUS } from "@/lib/constants";
import Boat from "@/components/icons/boat";
import { useTranslation } from "react-i18next";

const InvoiceCard = ({
  invoice,
  currencies,
}: {
  invoice: Invoice;
  currencies: Currency[] | undefined;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const currency = useMemo(() => {
    return currencies?.find((currency) => currency.id === invoice.currencyId);
  }, [currencies, invoice.currencyId]);

  const invoiceStatus = useMemo(() => {
    return INVOICE_STATUS.find((status) => status.value === invoice.status);
  }, [invoice.status]);

  return (
    <Card
      data-testid={`invoice-card-${invoice.id}`}
      onClick={() => {
        navigate(`/invoices/${invoice.id}`);
        Mixpanel.track("Navigate to Invoice Details");
      }}
    >
      <div className="flex flex-row items-center justify-center gap-2">
        <span className="flex items-center justify-center text-white/50">
          <Boat color="#ffffff50" size={20} />
        </span>

        <div className="flex flex-col items-start justify-start gap-2">
          {invoice.description && (
            <span className="text-sm font-medium leading-[14px] text-white">
              {invoice.description}
            </span>
          )}
          <div className="flex items-center gap-2">
            {invoice.status && (
              <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-medium text-white/80">
                {t(`invoice_status.${invoiceStatus?.translationKey}`)}
              </span>
            )}
            <span className="text-xs font-medium leading-[9px] text-white/50">
              {formatDate(invoice.issueDate, "dd MMMM yyyy, EE")}
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-col items-end gap-1">
        <div className="flex items-center gap-[6px]">
          <span className="text-sm font-medium leading-4 text-[#7ECEE8]">
            {currencyFormatter(invoice.totalAmount, currency?.code)}
          </span>
          <span style={{ color: invoiceStatus?.color }}>
            {invoiceStatus?.icon}
          </span>
        </div>
      </div>
    </Card>
  );
};

export default InvoiceCard;
